import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { hotelPath, hotelsPath } from 'app/hooks/data/hotels/useHotel';
import type { ApiErrorBody, HttpJsonError } from 'app/typings/error';
import { getErrorStatus, isHttpApiError } from 'app/utils/error';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

const GATEWAY_TIMEOUT = 504;
const NIGHTS_WITHOUT_REVENUE_SHARE_ERROR_CODE =
  'hotelMigrationNightsWithoutRevenueShare';

export type RoomWithoutRevenueShare = {
  oldRoomId: number;
  roomName: string | null;
  dates: string[];
};

type NightsWithoutRevenueShareErrorBody = ApiErrorBody & {
  data?: { rooms?: RoomWithoutRevenueShare[] };
};

export const isNightsWithoutRevenueShareError = (
  error: unknown
): error is HttpJsonError<NightsWithoutRevenueShareErrorBody> =>
  isHttpApiError(error) &&
  (error.jsonObject.errorCode as string) ===
    NIGHTS_WITHOUT_REVENUE_SHARE_ERROR_CODE;

export function useMigrateHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      hotelId,
      emailConfirmation,
      acceptedNightsWithoutRevenueShare = [],
    }: {
      hotelId: number;
      emailConfirmation: boolean;
      acceptedNightsWithoutRevenueShare?: Pick<
        RoomWithoutRevenueShare,
        'oldRoomId' | 'dates'
      >[];
    }) => {
      try {
        return (await apiMutation(
          'POST',
          {
            path: ENDPOINTS.migrateHotel,
            params: { id: hotelId.toString() },
          },
          { emailConfirmation, acceptedNightsWithoutRevenueShare }
        )) as void;
      } catch (error) {
        if (error instanceof TypeError) {
          throw new Error('Migration gateway timeout', {
            cause: { code: GATEWAY_TIMEOUT },
          });
        }

        throw error;
      }
    },
    onSuccess: (_, { hotelId }) => {
      message.success(`Hotel successfully migrated to the New Data Model.`);
      queryClient.invalidateQueries({
        queryKey: [hotelsPath],
        exact: false,
      });

      queryClient.invalidateQueries({ queryKey: [hotelPath, { hotelId }] });
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.hotelEntities, { id: hotelId.toString() }],
      });
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.hotelInfo, { id: hotelId }],
      });
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.hotels],
        exact: false,
      });
    },
    onError: (error, { hotelId }) => {
      if (isNightsWithoutRevenueShareError(error)) {
        return;
      }

      if (getErrorStatus(error) === GATEWAY_TIMEOUT) {
        message.warning(
          'The migration is taking longer than expected and is still running in the background. Refresh in a few minutes to check the result.'
        );

        return;
      }

      message.error(
        isHttpApiError(error)
          ? error.jsonObject.errorMessage
          : `Error while migrating hotel #${hotelId}`
      );
    },
  });
}
